

import axios from "axios"

// API endpoint for fetching questions
const API_BASE_URL = "/api"

/**
 * QuestionFetcher - Handles fetching, filtering, and processing questions
 */
const QuestionFetcher = {
  /**
   * Fetches questions from the API based on search text
   * @param {string} searchText - The search query
   * @param {Function} setResults - Optional setState function to update results
   * @returns {Promise<Array>} Array of questions
   */
  fetchQuestionData: async (searchText, setResults = null) => {
    try {
      if (!searchText || searchText.trim().length === 0) {
        if (setResults) setResults([])
        return []
      }

      const response = await axios.post(`${API_BASE_URL}/queryQuestionBank`, {
        searchText: searchText.trim(),
      })

      const data = response.data || []
      if (setResults) setResults(data)
      return data
    } catch (error) {
      console.error("Failed to fetch questions:", error)
      if (setResults) setResults([])
      throw new Error("Failed to fetch questions from the server")
    }
  },

  /**
   * Fetch a single question by ID
   * @param {string} questionId - The question ID
   * @returns {Promise<Object>} Question object
   */
  fetchQuestionById: async (questionId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/exam-question/${questionId}`)
      return response.data
    } catch (error) {
      console.error("Failed to fetch question by ID:", error)
      throw new Error("Failed to fetch question details")
    }
  },

  /**
   * Filters questions based on multiple criteria
   * @param {Array} questions - Array of questions to filter
   * @param {Object} filters - Filter criteria object
   * @returns {Array} Filtered questions
   */
  filterQuestions: (questions, filters = {}) => {
    if (!questions || questions.length === 0) {
      return []
    }

    return questions.filter((question) => {
      return Object.entries(filters).every(([key, value]) => {
        // Skip empty or "All" filters
        if (!value || value === "All" || value === "") {
          return true
        }

        // Handle different filter types
        switch (key) {
          case "subject":
            return question.subject === value
          case "examBoard":
            return question.examBoard === value
          case "syllabusCode":
            return question.syllabusCode === value
          case "yearOfExam":
            return question.yearOfExam === value || question.yearOfExam === Number.parseInt(value)
          case "session":
            return question.session === value
          default:
            return question[key] === value
        }
      })
    })
  },

  /**
   * Randomizes the order of questions in an array.
   * @param {Array} questions - The array of questions to randomize.
   * @returns {Array} - The randomized array of questions.
   */
  randomizeQuestions: (questions) => {
    if (!questions || questions.length === 0) {
      return []
    }

    // Create a copy to avoid mutating the original array
    const shuffled = [...questions]

    // Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }

    return shuffled
  },

  /**
   * Selects a specific number of questions from an array.
   * @param {Array} questions - The array of questions to select from.
   * @param {number} count - Number of questions to select
   * @param {boolean} randomize - Whether to randomize before selecting
   * @returns {Array} - The selected array of questions.
   */
  selectQuestions: (questions, count, randomize = false) => {
    if (!questions || questions.length === 0) {
      return []
    }

    const questionsToUse = randomize ? QuestionFetcher.randomizeQuestions(questions) : questions
    return questionsToUse.slice(0, Math.min(count, questionsToUse.length))
  },

  /**
   * Gets questions by their IDs from a larger array
   * @param {Array} questions - Array of all questions
   * @param {Array} questionIds - Array of question IDs to find
   * @returns {Array} Found questions
   */
  getQuestionsByIds: (questions, questionIds) => {
    if (!questions || !questionIds || questionIds.length === 0) {
      return []
    }

    return questions.filter((question) => questionIds.includes(question.id))
  },

  /**
   * Extracts unique values for a specific field from questions array
   * @param {Array} questions - Array of questions
   * @param {string} field - Field name to extract values from
   * @returns {Array} Unique values for the field
   */
  getUniqueFieldValues: (questions, field) => {
    if (!questions || questions.length === 0) {
      return ["All"]
    }

    const values = new Set(["All"])

    questions.forEach((question) => {
      if (question[field] && question[field] !== null && question[field] !== undefined) {
        values.add(question[field])
      }
    })

    const valuesArray = Array.from(values)

    // Special handling for year fields - sort in descending order
    if (field === "yearOfExam") {
      return valuesArray
        .filter((year) => year === "All" || typeof year === "number" || !isNaN(Number(year)))
        .sort((a, b) => {
          if (a === "All") return -1
          if (b === "All") return 1
          return b - a
        })
    }

    return valuesArray.sort((a, b) => {
      if (a === "All") return -1
      if (b === "All") return 1
      return a.toString().localeCompare(b.toString())
    })
  },

  /**
   * Gets filter categories from questions array
   * @param {Array} questions - Array of questions
   * @returns {Object} Object with filter categories and their values
   */
  getFilterCategories: (questions) => {
    const categoryKeys = ["examBoard", "syllabusCode", "subject", "yearOfExam", "session"]
    const categories = {}

    categoryKeys.forEach((key) => {
      categories[key] = QuestionFetcher.getUniqueFieldValues(questions, key)
    })

    return categories
  },

  /**
   * Searches questions with advanced options
   * @param {string} searchText - Search query
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results with metadata
   */
  searchQuestions: async (searchText, options = {}) => {
    try {
      const { filters = {}, randomize = false, limit = null, includeMetadata = true } = options

      // Fetch questions from API
      const allQuestions = await QuestionFetcher.fetchQuestionData(searchText)

      // Apply filters
      const filteredQuestions = QuestionFetcher.filterQuestions(allQuestions, filters)

      // Randomize if requested
      const processedQuestions = randomize ? QuestionFetcher.randomizeQuestions(filteredQuestions) : filteredQuestions

      // Apply limit if specified
      const finalQuestions = limit ? processedQuestions.slice(0, limit) : processedQuestions

      const result = {
        questions: finalQuestions,
        totalFound: allQuestions.length,
        filteredCount: filteredQuestions.length,
        returnedCount: finalQuestions.length,
      }

      // Add metadata if requested
      if (includeMetadata) {
        result.metadata = {
          subjects: QuestionFetcher.getUniqueFieldValues(allQuestions, "subject").filter((s) => s !== "All"),
          examBoards: QuestionFetcher.getUniqueFieldValues(allQuestions, "examBoard").filter((s) => s !== "All"),
          syllabusCodes: QuestionFetcher.getUniqueFieldValues(allQuestions, "syllabusCode").filter((s) => s !== "All"),
          years: QuestionFetcher.getUniqueFieldValues(allQuestions, "yearOfExam").filter((s) => s !== "All"),
          sessions: QuestionFetcher.getUniqueFieldValues(allQuestions, "session").filter((s) => s !== "All"),
        }
        result.filterCategories = QuestionFetcher.getFilterCategories(allQuestions)
      }

      return result
    } catch (error) {
      console.error("Search failed:", error)
      throw error
    }
  },

  /**
   * Validates question data
   * @param {Object} question - Question object to validate
   * @returns {boolean} Whether the question is valid
   */
  validateQuestion: (question) => {
    if (!question || typeof question !== "object") {
      return false
    }

    // Check for required fields
    const requiredFields = ["id", "questionTitle", "questionContent"]
    return requiredFields.every((field) => question[field] && question[field].toString().trim().length > 0)
  },

  /**
   * Processes solution content to handle different formats
   * @param {*} solutionContent - Solution content (could be object or string)
   * @returns {string} Processed solution content
   */
  processSolutionContent: (solutionContent) => {
    if (!solutionContent) {
      return ""
    }

    // If it's already a string, return as is
    if (typeof solutionContent === "string") {
      return solutionContent
    }

    // If it's an object with Parts property (structured solution)
    if (solutionContent.Parts && Array.isArray(solutionContent.Parts)) {
      return solutionContent.Parts.map((part) => {
        let markingScheme = part.MarkingScheme

        // Handle marking scheme that might be an object
        if (markingScheme && typeof markingScheme === "object") {
          try {
            markingScheme = JSON.stringify(markingScheme, null, 2)
          } catch (e) {
            markingScheme = "See detailed marking scheme in solution"
          }
        }

        return [part.Solution, markingScheme ? `\nMarking Scheme: ${markingScheme}\n` : ""].filter(Boolean).join("\n")
      }).join("\n\n")
    }

    // Try to stringify other objects
    try {
      return JSON.stringify(solutionContent, null, 2)
    } catch (e) {
      return "Error: Could not process solution content"
    }
  },

  /**
   * Batch processes multiple questions
   * @param {Array} questions - Array of questions to process
   * @returns {Array} Processed questions
   */
  batchProcessQuestions: (questions) => {
    if (!questions || !Array.isArray(questions)) {
      return []
    }

    return questions.filter(QuestionFetcher.validateQuestion).map((question) => ({
      ...question,
      processedSolution: QuestionFetcher.processSolutionContent(question.solutionContent),
      isValid: QuestionFetcher.validateQuestion(question),
    }))
  },

  /**
   * Searches questions with topics first, then applies filters with fallback
   * @param {string} searchText - The search query (topics)
   * @param {Object} filters - Filter criteria object
   * @returns {Promise<Object>} Search results with metadata
   */
  searchWithFallback: async (searchText, filters = {}) => {
    try {
      // Step 1: Search with topics first
      const allQuestions = await QuestionFetcher.fetchQuestionData(searchText)

      if (allQuestions.length === 0) {
        return {
          questions: [],
          totalFound: 0,
          filteredCount: 0,
          returnedCount: 0,
          usedFilters: false,
          fallbackUsed: false,
        }
      }

      // Step 2: Apply filters to search results
      const filteredQuestions = QuestionFetcher.filterQuestions(allQuestions, filters)

      // Step 3: Check if filtering produced results
      let finalQuestions = filteredQuestions
      let usedFilters = true
      let fallbackUsed = false

      // If no results after filtering, fall back to unfiltered search results
      if (filteredQuestions.length === 0 && Object.keys(filters).length > 0) {
        console.log("No results found with filters, falling back to unfiltered search results")
        finalQuestions = allQuestions
        usedFilters = false
        fallbackUsed = true
      }

      return {
        questions: finalQuestions,
        totalFound: allQuestions.length,
        filteredCount: filteredQuestions.length,
        returnedCount: finalQuestions.length,
        usedFilters,
        fallbackUsed,
        appliedFilters: usedFilters ? filters : {},
      }
    } catch (error) {
      console.error("Search with fallback failed:", error)
      throw error
    }
  },
}

export default QuestionFetcher
